import { ClipboardEvent, FC } from 'react';

import useDebounce from '@hh.ru/bloko/build/common/hooks/useDebounce';
import { SearchInput, useBreakpoint } from '@hh.ru/magritte-ui';

import useOnOffState from 'hooks/common/useOnOffState';
import { useRequestCancel } from 'hooks/common/useRequestCancel';
import useTrls from 'hooks/translation/useTrls';

import { fetchVacancySearch } from 'components/MultiCreationAuction/VacanciesFiltersPanel/fetchVacancySearch';

import { setMultiCreationVacanciesSearchValue } from 'store/models/multiCreation';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    searchPlaceholder: 'MultiCreationAuction.VacanciesSearchFilter.searchPlaceholder',
    searchPlaceholderMobile: 'MultiCreationAuction.VacanciesSearchFilter.searchPlaceholderMobile',
};

export const VacanciesSearchFilter: FC = () => {
    const vacancySearchValue = useSelector(({ multiCreation }) => multiCreation.vacancySearchValue);
    const [isPasted, setIsPastedOn, setIsPastedOff] = useOnOffState(false);

    const { abortRequest, abortSignal } = useRequestCancel();
    const trls = useTrls();
    const dispatch = useDispatch();
    const { isXS } = useBreakpoint();

    const debouncedSearch = useDebounce((newSearchValue: string) => {
        abortRequest();
        void dispatch(fetchVacancySearch({ nameFilter: newSearchValue, abortSignal: abortSignal() }));
    }, 1000);

    const onSearchValueChange = (newSearchValue: string) => {
        if (isPasted) {
            setIsPastedOff();
            return;
        }
        dispatch(setMultiCreationVacanciesSearchValue(newSearchValue));
        debouncedSearch(newSearchValue);
    };

    const onPaste = (event: ClipboardEvent<HTMLInputElement>) => {
        setIsPastedOn();

        const values = event.clipboardData?.getData('text').split(/\r\n|\n|\r/);
        const filteredValues: Set<string> = new Set();

        if (values) {
            values.forEach((value) => {
                if (value) {
                    filteredValues.add(value.trim());
                }
            });
        }

        const newSearchValue = [...filteredValues].join(',');

        dispatch(setMultiCreationVacanciesSearchValue(newSearchValue));
        debouncedSearch(newSearchValue);
    };

    return (
        <SearchInput
            data-qa="search-vacancies"
            onChange={onSearchValueChange}
            onPaste={onPaste}
            placeholder={trls(isXS ? TrlKeys.searchPlaceholderMobile : TrlKeys.searchPlaceholder)}
            value={vacancySearchValue}
            clearable
        />
    );
};
